import { Layout, Avatar, Space, Flex, Button, Menu, Dropdown, Modal, Input, message, ConfigProvider } from 'antd';
import { UserOutlined, MenuOutlined, DownOutlined, AppstoreOutlined, PlusOutlined, FolderOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import logo from '../../icons/Logo-white.svg';
import Logout from '../Logout';
import { useState, useEffect } from 'react';
import { list_user_workspaces, create_workspace, get_workspace_folder_url, getCookie} from '../../backendApi/backend';
import { useGlobalContext } from '../../GlobalContext';

const { Header: AntHeader } = Layout;

interface HeaderProps {
  isMobile?: boolean;
  onMenuClick?: () => void;
}

interface Workspace {
  id: string;
  name: string;
  role: string;
  is_owner: boolean;
}

function Header({ isMobile = false, onMenuClick }: HeaderProps) {
  const username = localStorage.getItem('user');
  const [currentWorkspace, setCurrentWorkspace] = useState('Undefined');
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [loading, setLoading] = useState(false);
  const { workspaceRefreshTrigger, refreshWorkspaces } = useGlobalContext();

  useEffect(() => {
    // Fetch workspaces when component mounts
    fetchWorkspaces();
  }, []);

  // Listen for workspace refresh trigger from global context
  useEffect(() => {
    if (workspaceRefreshTrigger > 0) {
      fetchWorkspaces();
    }
  }, [workspaceRefreshTrigger]);

  const fetchWorkspaces = async () => {
    try {
      const response = await list_user_workspaces();
      if (Array.isArray(response) && response.length > 0) {
        setWorkspaces(response);

        // Set the current workspace based on the local storage or the first workspace
        const storedWorkspaceId = localStorage.getItem('currentWorkspaceId');
        if (storedWorkspaceId) {
          const selectedWorkspace = response.find(workspace => workspace.id === storedWorkspaceId);
          if (selectedWorkspace) {
            setCurrentWorkspace(selectedWorkspace.name);
          } else {
            // Stored workspace doesn't exist for this user, default to first
            setCurrentWorkspace(response[0].name);
            localStorage.setItem('currentWorkspaceId', response[0].id);
          }
        } else {
          // Default to the first workspace if no stored ID

          setCurrentWorkspace(response[0].name);
          localStorage.setItem('currentWorkspaceId', response[0].id);
        }
      }
    } catch (error) {
      console.error('Error fetching workspaces:', error);
    }
  };

  const handleWorkspaceChange = (id: string) => {
    const selected = workspaces.find(workspace => workspace.id === id);
    if (selected) {
      setCurrentWorkspace(selected.name);
      // Store current workspace ID in localStorage or context for app-wide access
      localStorage.setItem('currentWorkspaceId', selected.id);
    }
  };

  const handleFolderRedirect = async () => {
    try {
      const azureadToken = getCookie('azuread_token');
      const userEmail = localStorage.getItem('user');
      
      if (!azureadToken) {
        message.error('Please connect to Microsoft OneDrive first');
        setTimeout(() => {window.location.href = '/settings';}, 700);
        return;
      }

      if (!userEmail) {
        message.error('User not found. Please log in again.');
        return;
      }

      const response = await get_workspace_folder_url(currentWorkspace, userEmail, azureadToken);
      if (response.folder_url) {
        window.open(response.folder_url, '_blank');
      } else {
        message.error('Workspace folder not found');
      }
    } catch (error) {
      console.error('Error opening workspace folder:', error);
      message.error('Failed to open workspace folder');
    }
  };

  const showCreateModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setNewWorkspaceName('');
  };

  const handleCreate = async () => {
    if (!newWorkspaceName.trim()) {
      message.error('Workspace name cannot be empty');
      return;
    }

    setLoading(true);
    try {
      const response = await create_workspace(newWorkspaceName);
      message.success('Workspace created successfully');
      setIsModalVisible(false);
      setNewWorkspaceName('');
      localStorage.setItem('currentWorkspaceId', response.id);
      
      // Trigger global workspace refresh and fetch updated workspaces
      refreshWorkspaces();
      await fetchWorkspaces();
    } catch (error: any) {
      if (error.response?.status === 401) {
        message.error('Microsoft login is required to create workspace');
      } else {
        message.error('Failed to create workspace');
      }
      if (error.response?.status === 403 && error.response?.data?.error?.includes('SharePoint license is missing')) {
        message.error('You do not have SPO license to create a workspace.');
      } else {
        message.error(error.response?.data?.error || 'Failed to create workspace. Please try again.');
      }
      console.error('Error creating workspace:', error);
    } finally {
      setLoading(false);
    }
  };

  const workspaceMenu = (
    <ConfigProvider
        theme={{
          token: {
            colorPrimary: 'var(--primary-color)',
            colorTextBase: 'var(--text-color)',
            colorText: 'var(--text-color)',
            colorTextHeading: 'var(--text-color)',
            colorTextDescription: 'var(--text-color)',
          },
        }}
      >
    <Menu
      theme='dark'
      items={[
        ...workspaces.map(workspace => ({
          key: workspace.id,
          label: workspace.name,
          onClick: () => handleWorkspaceChange(workspace.id)
        })),
        {
          key: 'create',
          label: (
            <span style={{ color: 'var(--text-dark)', background: 'transparent' }}>
              <PlusOutlined /> Create Workspace
            </span>
          ),
          onClick: showCreateModal
        }
      ]}
    />
    </ConfigProvider>
  );

  return (
    <>
      <AntHeader style={{ 
        background: 'var(--primary-bg-color)', 
        padding: isMobile ? '10px 16px' : '50px 200px',
        position: 'fixed',
        width: '100%',
        zIndex: 1000,
        height: isMobile ? 56 : 64,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
      }}>
        <Flex align="center" justify="center" style={{ height: '100%', width: '100%' }}>

          {/* Logo and Menu Button */}
          <Flex align="center" gap="large" style={{ width: '100%' }}>
            {isMobile && (
              <Button 
                type="text" 
                icon={<MenuOutlined style={{ color: 'var(--text-color)' }} />} 
                onClick={onMenuClick}
                style={{ padding: '4px 8px' }}
              />
            )}
            
            <Link to="/" style={{ display: 'flex', alignItems: 'center', alignContent: 'center' }}>
              <img 
                src={logo} 
                alt="Logo" 
                style={{ 
                  height: isMobile ? 64: 116, 
                  width: 'auto',
                }} 
              />
            </Link>
          </Flex>


          <Flex gap={isMobile ? "small" : "middle"} align="center">
          
              <Dropdown overlay={workspaceMenu} trigger={['click']}>
                <a 
                  onClick={e => e.preventDefault()}
                  style={{ 
                    color: 'var(--primary-color)',
                    display: 'flex',
                    alignItems: 'center',
                    width: 'fit-content',
                    padding: '4px 8px',
                    textWrap: 'nowrap',
                  }}
                >
                  <AppstoreOutlined style={{ marginRight: 8 }} />
                  <span style={{ marginRight: 8, width: 'fit-content' }}>{currentWorkspace}</span>
                  <DownOutlined style={{ fontSize: 12 }} />
                </a>
              </Dropdown>

              <Button
                type="text"
                icon={<FolderOutlined />}
                onClick={handleFolderRedirect}
                style={{
                  color: 'var(--primary-color)',
                  border: 'none',
                  padding: '4px 8px',
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: isMobile ? 14 : 16
                }}
                title="Open workspace folder"
              />

            {/* User Profile and Logout */}

          
            <Space size={isMobile ? 4 : 8} style={{ color: 'var(--primary-color)' }}>
              {!isMobile && (
                <Avatar
                  style={{ 
                    backgroundColor: 'transparent', 
                    color: 'var(--primary-color)', 
                    fontSize: isMobile ? 12 : 16,
                    width: isMobile ? 24 : 32,
                    height: isMobile ? 24 : 32,
                  }}
                  size={isMobile ? "small" : "default"} 
                  icon={<UserOutlined />} 
                />
              )}
                {!isMobile && (
                  <span style={{ 
                    color: 'var(--primary-color)',
                    fontSize: '14px'
                  }}>
                    {username}
                  </span>
                )}
            </Space>

            {!isMobile && (
              <Logout />)
            }
          </Flex>
        </Flex>
      </AntHeader>

      {/* Modal for creating new workspace */}
      <Modal
        title={<span style={{ color: 'var(--text-color)' }}>Create New Workspace</span>}
        open={isModalVisible}
        onOk={handleCreate}
        onCancel={handleCancel}
        confirmLoading={loading}
        okButtonProps={{ 
          style: { 
            backgroundColor: 'var(--primary-color)',
            borderColor: 'var(--primary-color)',
            color: 'var(--text-dark)',
            fontWeight: 500
          } 
        }}
        cancelButtonProps={{ 
          style: { 
            backgroundColor: 'var(--primary-color)',
            borderColor: 'var(--primary-color)',
            color: 'var(--text-dark)',
            fontWeight: 500
          } 
        }}
        centered
        styles={{
          mask: {
            backgroundColor: 'rgba(0, 0, 0, 0.65)'
          },
          content: {
            backgroundColor: 'var(--primary-bg-color)',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
          },
          header: {
            borderBottom: '1px solid #333',
            padding: '16px 24px',
            backgroundColor: 'var(--primary-bg-color)',
          },
          body: {
            padding: '24px',
            backgroundColor: 'var(--primary-bg-color)',
          },
          footer: {
            borderTop: '1px solid #333',
            padding: '12px 24px',
            backgroundColor: 'var(--primary-bg-color)',
          }
        }}
      >
        <p style={{ color: 'var(--text-light)', marginBottom: '16px' }}>
          Create a new workspace to extend knowledge base and share with team members.
        </p>
        <Input
          placeholder="Enter workspace name"
          value={newWorkspaceName}
          onChange={(e) => setNewWorkspaceName(e.target.value)}
          style={{ 
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            borderColor: '#333',
            borderRadius: '6px',
            padding: '8px 12px',
            color: 'var(--text-color)'
          }}
          className='password-input-custom'
          prefix={<AppstoreOutlined style={{ color: 'var(--primary-color)', marginRight: '8px' }} />}
          autoFocus
        />
      </Modal>
    </>
  );
}

export default Header;